import { useMutation } from "react-query";
import { useAppContext } from "../AppProviders";
import { countFilterQuery } from "./countFilterQuery";
import { API_URL, isMocksEnabled } from "./helpers";
import { XPage, xpageNextOffset } from "./GenericModels";
import {
  SocialMediaScraperExtId,
  GoogleScraperExtId,
  SalvageYardID,
} from "../constants/FamaExtension";
import {
  CreateItemPostRatingRequest,
  CreateManualScrapePostResponse,
  CreatePostApi,
  FlagCountRequest,
  FlagCountResponse,
  ItemsForReviewRequest,
  ItemsForReviewResponse,
  ItemsForSummaryRequest,
  ItemWithMetaData,
  KeywordsCountRequest,
  ItemTypes,
  RemovePostItemRequest,
  UpdateItemPostRatingRequest,
  UpdateItemPostRatingResponse,
  GroupCount,
  ArticlesForReviewRequest,
  CreateArticleRatingRequest,
  CreateArticleApi,
  Item,
  UpdateWebContentApi,
  CreateItemPostRatingResponse,
  MediaRequest,
  Translation,
  TranslationResponse,
  CreateAffiliationApi,
  UpdateAffiliationApi,
  RestorePostItemRequest,
  ItemSource,
  TopicCount,
  TopicCountRequest,
} from "./Items";
import {
  fetchProfileList,
  fetchPostRateList,
} from "../__mocks__/itemsForReviewList";
import {
  createPostsItemRatingMock,
  createPostsMock,
  removePostsItemMock,
  updatePostsItemRatingMock,
} from "../__mocks__/itemsOps";
import { useRef, useState } from "react";
import { RequestApi } from ".";
import { getChromeApi } from "../services/extension";

export interface CreateExtensionScrapedPostRequest {
  report_uuid: string;
}

export interface ExtensionPostMediaEntry {
  mediaUrl: string;
  mediaType: string;
  thumbnailUrl: string;
  famaUrl: string;
}

export interface ExtensionPost {
  profileUrl: string;
  date: string;
  mediaEntries: ExtensionPostMediaEntry[];
  postType: string;
  siteId: string;
  text: string;
  url: string;
}

/** Matches Profile Confirm; items_for_review defaults to page_size=10 server-side. */
export const RERUN_PROFILE_PREFILL_PAGE_SIZE = 100;
export const RERUN_PROFILE_PREFILL_MAX_PAGES = 100;

export type ItemsForReviewFetchInput = ItemsForReviewRequest & {
  /**
   * When true, page through items_for_review until all rows are loaded.
   * Used by hybrid-rerun prefill so client-provided profiles older than the
   * newest page are not omitted from the form (and then dropped by EI merge).
   * Not forwarded as a query param.
   */
  fetch_all?: boolean;
};

/** Page through items_for_review until ``total`` is exhausted (or max pages).

 * Uses ``page_num`` (not ``page``): ``GET /item/items_for_review`` in fama-external-interface
 * binds ``page_num``. Sending only ``page`` always returns the first page.
 *
 * Dedupes by ``external_id``. Same URL across different ids keeps / prefers the
 * ``in_report`` row so hybrid rerun prefill (which filters to in_report) does not
 * drop a provided profile when a non-in_report sibling appears first.
 * Stops when a page contributes no new/better rows — same spirit as Profile Confirm.
 */
export async function fetchAllItemsForReviewPages(
  getPage: (params: ItemsForReviewRequest) => Promise<ItemsForReviewResponse>,
  apiInput: ItemsForReviewRequest,
  options?: { pageSize?: number; maxPages?: number }
): Promise<ItemsForReviewResponse> {
  const pageSize =
    options?.pageSize ?? apiInput.page_size ?? RERUN_PROFILE_PREFILL_PAGE_SIZE;
  const maxPages = options?.maxPages ?? RERUN_PROFILE_PREFILL_MAX_PAGES;
  const allItems: ItemWithMetaData[] = [];
  const seenIds = new Set<string>();
  const urlIndex = new Map<string, number>();
  let total = Number.POSITIVE_INFINITY;
  let page = 1;

  while (page <= maxPages) {
    const response = await getPage({
      ...apiInput,
      page_num: page,
      page_size: pageSize,
    });
    if (!response) {
      break;
    }
    total = typeof response.total === "number" ? response.total : 0;
    const batch = response.items ?? [];
    if (batch.length === 0) {
      break;
    }
    let freshCount = 0;
    for (const entry of batch) {
      const id = entry.item?.external_id;
      const url = String(entry.item?.content?.url ?? "").trim();
      const inReport = Boolean(entry.item?.in_report);

      if (id && seenIds.has(id)) {
        continue;
      }

      if (url && urlIndex.has(url)) {
        const existingIdx = urlIndex.get(url)!;
        const existing = allItems[existingIdx];
        const existingInReport = Boolean(existing.item?.in_report);
        // Prefer the in_report copy for prefill; never replace an in_report with a non-in_report.
        if (inReport && !existingInReport) {
          const existingId = existing.item?.external_id;
          if (existingId) {
            seenIds.delete(existingId);
          }
          allItems[existingIdx] = entry;
          if (id) {
            seenIds.add(id);
          }
          freshCount += 1;
        }
        continue;
      }

      if (id) {
        seenIds.add(id);
      }
      if (url) {
        urlIndex.set(url, allItems.length);
      }
      allItems.push(entry);
      freshCount += 1;
    }
    // Same page repeated (wrong param / stuck offset): stop instead of duplicating forever.
    if (freshCount === 0) {
      break;
    }
    // Advance by reported page count, not deduped length (URL prefer can shrink the list).
    if (Number.isFinite(total) && page * pageSize >= total) {
      break;
    }
    page += 1;
  }

  return {
    items: allItems,
    total: allItems.length,
    page: 1,
    size: allItems.length,
  };
}

export function useFetchItemsForReview() {
  const { requestApi } = useAppContext();

  const getItemsForReview = async (
    input: ItemsForReviewFetchInput
  ): Promise<ItemsForReviewResponse> => {
    const { fetch_all, ...apiInput } = input;
    if (isMocksEnabled()) {
      const func = apiInput.item_type_name.includes(
        ItemTypes?.SOCIAL_MEDIA_PROFILE
      )
        ? fetchProfileList
        : fetchPostRateList;
      return await func();
    }

    const getPage = (params: ItemsForReviewRequest) =>
      requestApi
        .get(`${API_URL}/item/items_for_review`, params)
        .then((response) => response.data as ItemsForReviewResponse);

    if (!fetch_all) {
      return getPage(apiInput);
    }

    return fetchAllItemsForReviewPages(getPage, apiInput, {
      pageSize: apiInput.page_size ?? RERUN_PROFILE_PREFILL_PAGE_SIZE,
    });
  };

  const mutationResult = useMutation(
    (input: ItemsForReviewFetchInput) => getItemsForReview(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useFetchItemsForRate() {
  const { requestApi } = useAppContext();
  const getItemsForRate = async (
    input: ItemsForReviewRequest
  ): Promise<ItemWithMetaData[]> => {
    return requestApi
      .get(`${API_URL}/report/items_for_rate`, input)
      .then((response) => {
        const data = response.data;
        if (Array.isArray(data)) {
          return data;
        }
        if (
          data != null &&
          typeof data === "object" &&
          Array.isArray((data as { items?: unknown }).items)
        ) {
          return (data as { items: ItemWithMetaData[] }).items;
        }
        return [];
      });
  };
  const mutationResult = useMutation(
    (input: ItemsForReviewRequest) => getItemsForRate(input),
    {}
  );
  return {
    ...mutationResult,
  };
}

export function useFetchItemsForSummary() {
  const { requestApi } = useAppContext();

  const getItemsForSummary = async (
    input: ItemsForSummaryRequest
  ): Promise<ItemWithMetaData[]> => {
    const size: number = input.page_size || 50;
    let offset: number = (input.page_number - 1) * size;
    const target = offset + size;
    let output: ItemWithMetaData[] = [];
    while (offset < target) {
      let remaining = target - offset;
      const response = await requestApi.post(
        `${API_URL}/report/items_for_summary?offset=${offset}&size=${remaining}`,
        input
      );
      const page: XPage<ItemWithMetaData> = response.data;
      if (page.data) {
        output = output.concat(page.data);
      }
      const nextOffset = xpageNextOffset(page, offset);
      if (nextOffset === null) {
        break;
      }
      offset = nextOffset;
    }
    return output;
  };

  const mutationResult = useMutation(
    (input: ItemsForSummaryRequest) => getItemsForSummary(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useFetchFlagCount() {
  const { requestApi } = useAppContext();

  const getFlagCount = async (
    input: FlagCountRequest
  ): Promise<FlagCountResponse> => {
    return requestApi
      .get(
        `${API_URL}/reports/flag_count/${input.report_uuid}?${countFilterQuery(
          input
        )}`
      )
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (input: FlagCountRequest) => getFlagCount(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useFetchKeywordsCount() {
  const { requestApi } = useAppContext();

  const getKeywordsCount = async (
    input: KeywordsCountRequest
  ): Promise<GroupCount> => {
    return requestApi
      .get(
        `${API_URL}/reports/keyword_count/${input.report_uuid}?${countFilterQuery(
          input
        )}`
      )
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (input: KeywordsCountRequest) => getKeywordsCount(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useFetchTopicsCount() {
  const { requestApi } = useAppContext();

  const getTopicsCount = async (
    input: TopicCountRequest
  ): Promise<TopicCount> => {
    return requestApi
      .get(
        `${API_URL}/reports/topic_count/${input.report_uuid}?${countFilterQuery(
          input
        )}`
      )
      .then((response) => response.data);
  };

  return useMutation((input: TopicCountRequest) => getTopicsCount(input), {});
}

export function useFetchKeywordsOptions() {
  const { requestApi } = useAppContext();

  const getKeywordsOptions = async (report_uuid: string): Promise<string[]> => {
    return requestApi
      .get(`${API_URL}/reports/keyword_options/${report_uuid}`)
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (report_uuid: string) => getKeywordsOptions(report_uuid),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useFetchSystemPostsItemsForReview() {
  const { requestApi } = useAppContext();

  const getItemsForReview = async (
    input: ItemsForReviewRequest
  ): Promise<ItemsForReviewResponse> => {
    if (isMocksEnabled()) {
      const func = input.item_type_name.includes(
        ItemTypes?.SOCIAL_MEDIA_PROFILE
      )
        ? fetchProfileList
        : fetchPostRateList;
      return await func();
    }

    return requestApi
      .post(`${API_URL}/item/items_for_review`, input)
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (input: ItemsForReviewRequest) => getItemsForReview(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useRemovePostsItem() {
  const { requestApi } = useAppContext();

  const removePostsItem = async (
    input: RemovePostItemRequest
  ): Promise<string> => {
    if (isMocksEnabled()) {
      return await removePostsItemMock(input);
    }

    const requestInput: CreateItemPostRatingRequest = {
      item_uuid: input.item_uuid,
      ratings: [],
    };

    return requestApi
      .post(`${API_URL}/rating`, requestInput)
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (input: RemovePostItemRequest) => removePostsItem(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useRestorePostItemRatings() {
  const { requestApi } = useAppContext();

  const restorePostsItem = async (
    input: RestorePostItemRequest
  ): Promise<string> => {
    return requestApi
      .post(`${API_URL}/rating/restore/${input.item_uuid}`)
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (input: RestorePostItemRequest) => restorePostsItem(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useRemoveRatingAndKeywordsPostsItem() {
  const { requestApi } = useAppContext();

  const removeRatingsAndKeywords = async (input: {
    item_uuids: string[];
  }): Promise<string> => {
    return requestApi
      .post(`${API_URL}/rating/remove`, input.item_uuids)
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (input: { item_uuids: string[] }) => removeRatingsAndKeywords(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useCreateItemPostRating() {
  const { requestApi } = useAppContext();

  const createPostsItemRating = async (
    input: CreateItemPostRatingRequest
  ): Promise<CreateItemPostRatingResponse> => {
    if (isMocksEnabled()) {
      return await createPostsItemRatingMock(input);
    }

    return requestApi.post(`${API_URL}/rating`, input).then((response) => {
      return response.data;
    });
  };

  const mutationResult = useMutation(
    (input: CreateItemPostRatingRequest) => createPostsItemRating(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

async function createPostsItem(
  requestApi: RequestApi,
  input: CreatePostApi
): Promise<string> {
  if (isMocksEnabled()) {
    return await createPostsMock(input);
  }

  return requestApi
    .post(`${API_URL}/manual_create_posts_item`, input)
    .then((response) => {
      if (response.data) {
        return response.data.external_id;
      }
    });
}

export function useCreateItemPost() {
  const { requestApi } = useAppContext();

  const mutationResult = useMutation(
    (input: CreatePostApi) => createPostsItem(requestApi, input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useCreateItemArticle() {
  const { requestApi } = useAppContext();

  function createWebcontent(input: CreateArticleApi) {
    return requestApi
      .post(`${API_URL}/manual_create_web_content`, input)
      .then((response) => {
        if (response.data) {
          return response.data.external_id;
        }
      });
  }

  const mutationResult = useMutation(
    (input: CreateArticleApi) => createWebcontent(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useCreateItemAffiliation() {
  const { requestApi } = useAppContext();

  function createAffiliation(input: CreateAffiliationApi) {
    return requestApi
      .post(`${API_URL}/manual_create_affiliation`, input)
      .then((response) => {
        if (response.data) {
          return response.data.external_id;
        }
      });
  }

  const mutationResult = useMutation(
    (input: CreateAffiliationApi) => createAffiliation(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useUpdateItemAffiliation() {
  const { requestApi } = useAppContext();

  function updateAffiliation(input: UpdateAffiliationApi) {
    return requestApi
      .put(`${API_URL}/manual_update_affiliation`, input)
      .then((response) => {
        if (response.data) {
          return response.data.external_id;
        }
      });
  }

  const mutationResult = useMutation(
    (input: UpdateAffiliationApi) => updateAffiliation(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useUpdateItemArticle() {
  const { requestApi } = useAppContext();

  const updateWebcontent = async (
    input: UpdateWebContentApi
  ): Promise<Item> => {
    return requestApi
      .put(`${API_URL}/manual_update_web_content`, input)
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (input: UpdateWebContentApi) => updateWebcontent(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useUpdateItemPostRating() {
  const { requestApi } = useAppContext();

  const updatePostsItemRating = async (
    input: UpdateItemPostRatingRequest
  ): Promise<UpdateItemPostRatingResponse> => {
    if (isMocksEnabled()) {
      return await updatePostsItemRatingMock(input);
    }

    return requestApi.post(`${API_URL}/rating`, input).then((response) => {
      return response.data;
    });
  };

  const mutationResult = useMutation(
    (input: UpdateItemPostRatingRequest) => updatePostsItemRating(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useExtensionPost() {
  const { requestApi } = useAppContext();
  // chrome runtime only available when extension is installed
  // and its manifest gives externally connectable permissions
  // to this site.
  const scraperInstalled = !!getChromeApi()?.runtime;
  const [storingPosts, setStoringPosts] = useState<boolean>(false);
  const [error, setError] = useState<string>();
  const [success, setSuccess] = useState<boolean>(false);
  const [storedPosts, setStoredPosts] = useState<
    CreateManualScrapePostResponse[]
  >([]);

  type ExtensionPostInput = {
    created_at: string;
    mediaEntries: ExtensionPostMediaEntry[];
    postType: string;
    siteId: string;
    text: string;
    url: string;
  };

  async function initScrape(
    reportUUID: string,
    itemUrl: string,
    profileUUID: string
  ) {
    const chromeApi = getChromeApi();
    setStoringPosts(false);
    setSuccess(false);
    setStoredPosts([]);
    setError(undefined);

    if (!chromeApi?.runtime?.sendMessage) {
      setError("Browser extension messaging is unavailable.");
      return;
    }

    const interval = setInterval(() => {
      chromeApi.runtime.sendMessage(
        SocialMediaScraperExtId,
        "INIT",
        async (response: any) => {
          if (response.ready) {
            await startScrape();
            clearInterval(interval);
          }
        }
      );
    }, 5000);

    const startScrape = async () => {
      chromeApi.runtime.sendMessage(
        SocialMediaScraperExtId,
        "START",
        async (response: any) => {
          storeResultsCallback(
            response.profile,
            response.posts,
            response.isLastMessage
          ).then(() => {});
          startScrape();
        }
      );
    };

    const storeResultsCallback = async (
      scrapedUrl: string,
      extensionPostList: ExtensionPostInput[],
      isLastMessage: boolean = false
    ) => {
      const createBatches = (items: any[], batchSize: number) => {
        const batches = [];
        for (let i = 0; i < items.length; i += batchSize) {
          batches.push(items.slice(i, i + batchSize));
        }
        return batches;
      };

      const savePostBatch = async (batch: CreatePostApi[]) => {
        const response = await requestApi.post(
          `${API_URL}/manual_scrape/items`,
          batch
        );
        return response.data;
      };

      const retry = async (fn: Function, maxRetries: number) => {
        for (let i = 0; i < maxRetries; i++) {
          try {
            return await fn();
          } catch (error) {
            console.error(error);
            if (i < maxRetries - 1) {
              await new Promise((res) => setTimeout(res, i * 1000));
            }
          }
        }
        throw new Error("Max retries reached");
      };

      const sanitizeUrl = (url: string) => {
        const params =
          url.split("?").length > 1 ? url.split("?")[1].split("&").sort() : [];
        const sanitizedParams = params
          .filter(
            (param) => !POST_URL_PARAMS_BLACKLIST.includes(param.split("=")[0])
          )
          .join("&");
        return `${url.split("?")[0]}?${sanitizedParams}`;
      };

      const MAX_POST_TEXT_LENGTH = 2000;
      const BATCH_SIZE = 50;
      const MAX_RETRIES = 5;
      const POST_URL_PARAMS_BLACKLIST = [
        "eav",
        "m_entstream_source",
        "_ft_",
        "__tn__",
        "paipv",
        "refid",
      ];

      const batches = createBatches(
        extensionPostList.filter((p) => p.url.trim().length > 0),
        BATCH_SIZE
      ).map((batch) =>
        batch.map((payload) => ({
          ...payload,
          url: sanitizeUrl(payload.url),
          media: payload.media
            .map((m: MediaRequest) => {
              if (!m.external_url) return null;
              if (!m.internal_url) return null;
              return m;
            })
            .filter((p: MediaRequest) => p),
          text: payload.text.substring(0, MAX_POST_TEXT_LENGTH),
          report_uuid: reportUUID,
          profile_uuid: profileUUID,
        }))
      );

      const savedPosts: any[][] = [];
      const saveAndRetry = async (batch: CreatePostApi[]) => {
        try {
          const savedBatch = await retry(
            () => savePostBatch(batch),
            MAX_RETRIES
          );
          savedPosts.push(savedBatch);
        } catch (error) {
          throw error;
        }
      };

      try {
        setStoringPosts(true);
        await Promise.all(batches.map(saveAndRetry));
        const flattenedPosts = savedPosts.flat();
        setStoredPosts((prevPosts) => [...prevPosts, ...flattenedPosts]);
        if (isLastMessage) {
          setSuccess(true);
        }
      } catch (error) {
        console.error("Could not save batch", error);
        setSuccess(false);
      }

      setStoringPosts(false);
    };
  }

  return {
    initScrape,
    scraperInstalled,
    storingPosts,
    error,
    success,
    storedPosts,
  };
}

export function useFetchArticlesForReview() {
  const { requestApi } = useAppContext();

  const getItemsForReview = async (
    input: ArticlesForReviewRequest
  ): Promise<ItemsForReviewResponse> => {
    return requestApi
      .get(`${API_URL}/item/articles_for_review`, input)
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (input: ArticlesForReviewRequest) => getItemsForReview(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useUpdateArticleRating() {
  const { requestApi } = useAppContext();

  const updateArticleRating = async (
    input: CreateArticleRatingRequest
  ): Promise<UpdateItemPostRatingResponse> => {
    return requestApi
      .post(`${API_URL}/article/rating`, input)
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (input: CreateArticleRatingRequest) => updateArticleRating(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useUpdateManyArticleRating() {
  const { requestApi } = useAppContext();

  const updateArticleRating = async (
    input: CreateArticleRatingRequest[]
  ): Promise<UpdateItemPostRatingResponse> => {
    return requestApi
      .post(`${API_URL}/articles/rating`, input)
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (input: CreateArticleRatingRequest[]) => updateArticleRating(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useGetWebContentScrapeRecord() {
  const { requestApi } = useAppContext();

  const getWebContentScrapeRecord = async (
    reportUuid: string
  ): Promise<null | Array<any>> => {
    const response = await requestApi.get(
      `${API_URL}/reports/scrapes/${reportUuid}`
    );
    const scrapes = response.data;
    return (
      scrapes.filter((scrape: any) =>
        scrape.scraper_tool_version.startsWith("WCSS_")
      ) ?? scrapes
    );
  };

  const mutationResult = useMutation(
    (reportUuid: string) => getWebContentScrapeRecord(reportUuid),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useManuallyScrapeWebContent() {
  const { requestApi } = useAppContext();
  const scraperInstalled = !!getChromeApi()?.runtime;
  const [waitingForScraperReady, setWaitingForScraperReady] =
    useState<boolean>(false);
  const [readyToScrape, setReadyToScrape] = useState<boolean>(false);
  const [savingWebContent, setSavingWebContent] = useState<boolean>(false);
  const [savedWebContent, setSavedWebContent] = useState<CreateArticleApi[]>(
    []
  );
  const [failedWebContent, setFailedWebContent] = useState<CreateArticleApi[]>(
    []
  );

  type ExtensionWebContent = {
    url: string;
    title?: string;
    text?: string;
    created_at?: string;
  };

  function createWebcontent(input: CreateArticleApi) {
    return requestApi
      .post(`${API_URL}/manual_create_web_content`, input)
      .then((response) => {
        if (response.data) {
          return response.data.external_id;
        }
      });
  }

  async function initScrape(
    reportUuid: string,
    profileUuid: string,
    personName: string
  ) {
    const chromeApi = getChromeApi();
    setWaitingForScraperReady(true);
    setReadyToScrape(false);
    setSavingWebContent(false);
    setSavedWebContent([]);
    setFailedWebContent([]);

    if (!chromeApi?.runtime?.sendMessage) {
      console.warn(
        "[WebScrape] Browser extension messaging is unavailable; skipping manual web content scrape init"
      );
      setWaitingForScraperReady(false);
      setReadyToScrape(false);
      return;
    }

    const interval = setInterval(() => {
      chromeApi.runtime.sendMessage(
        GoogleScraperExtId,
        { message: "WEBSCRAPE_INIT" },
        async (response: any) => {
          if (response.ready) {
            await startScrape();
            clearInterval(interval);
          }
        }
      );
    }, 5000);

    const startScrape = async () => {
      chromeApi.runtime.sendMessage(
        GoogleScraperExtId,
        { message: "WEBSCRAPE_START", personName: personName },
        async (response: any) => {
          await storeResultsCallback(response?.article);
          await startScrape();
        }
      );
      setWaitingForScraperReady(false);
      setReadyToScrape(true);
    };

    const storeResultsCallback = async (result: ExtensionWebContent) => {
      setSavingWebContent(true);
      if (!result?.url) {
        return;
      }

      const article: CreateArticleApi = {
        report_uuid: reportUuid,
        profile_uuid: profileUuid,
        url: result.url,
        title: result.title || "",
        created_at: result.created_at || new Date().toISOString(),
        text: result.text?.substring(0, 2048) || "",
        score: 0,
        action: "review",
        status: "TO_DO",
        ratings: [],
      };

      try {
        createWebcontent(article);
        setSavedWebContent((prev: CreateArticleApi[]) => [...prev, article]);
      } catch (exception) {
        console.error("Error saving manually scraped web content", exception);
        setFailedWebContent((prev: CreateArticleApi[]) => [...prev, article]);
      }

      setSavingWebContent(false);
    };
  }

  return {
    initScrape,
    scraperInstalled,
    waitingForScraperReady,
    readyToScrape,
    savingWebContent,
    savedWebContent,
    failedWebContent,
  };
}

export function useCreateWebContentParent() {
  const { requestApi } = useAppContext();

  const createWebContentParent = async (report_uuid: string): Promise<Item> => {
    return requestApi
      .post(`${API_URL}/reports/webcontent/parent/${report_uuid}`)
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (report_uuid: string) => createWebContentParent(report_uuid),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useGetTranslations() {
  const { requestApi } = useAppContext();

  const getTranslations = async (item_uuid: string): Promise<Translation[]> => {
    return requestApi
      .get(`${API_URL}/item/translation`, { item_uuid })
      .then((response) => {
        return response.data.map((translation: TranslationResponse) => ({
          ...translation,
          original: JSON.parse(translation.original),
          translated: JSON.parse(translation.translated),
        }));
      });
  };

  const mutationResult = useMutation(
    (item_uuid: string) => getTranslations(item_uuid),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useGetItemSources() {
  const { requestApi } = useAppContext();
  const getItemSources = async (): Promise<ItemSource[]> => {
    return requestApi.get(`${API_URL}/itemdal/itemsources`).then((response) => {
      return response.data;
    });
  };
  const mutationResult = useMutation(() => getItemSources(), {});
  return { ...mutationResult };
}

export function useSalvageYardScrape() {
  const { requestApi } = useAppContext();
  const [isSalvageYardConnected, setIsSalvageYardConnected] = useState(false);
  const [storingPosts, setStoringPosts] = useState<boolean>(false);
  const [error, setError] = useState<string>();
  const [success, setSuccess] = useState<boolean>(false);
  const [storedPosts, setStoredPosts] = useState<
    CreateManualScrapePostResponse[]
  >([]);
  const portRef = useRef<any | null>(null);
  const pendingSavesRef = useRef<number>(0);
  const expectedProfileUrlRef = useRef<string | null>(null);
  const [skippedPosts, setSkippedPosts] = useState<
    { url: string; text?: string; reason?: string }[]
  >([]);

  const salvageYardScrape = async (
    reportUUID: string,
    profileUUID: string,
    profileContent: any,
    blueprint?: any
  ): Promise<any> => {
    setError(undefined);
    setSuccess(false);
    setStoredPosts([]);
    setSkippedPosts([]);
    expectedProfileUrlRef.current = profileContent?.url || null;

    const chromeApi = getChromeApi();
    if (!chromeApi?.runtime?.connect) {
      setError("Chrome extension messaging is unavailable.");
      throw new Error("chrome.runtime.connect unavailable");
    }
    console.info("[SalvageYard] chrome API available");

    const port = chromeApi.runtime.connect(SalvageYardID, { name: "ui<->bg" });
    if (!port) {
      setError("Failed to open a port to the extension.");
      throw new Error("Failed to connect to extension");
    }
    console.info("[SalvageYard] port opened", port);

    portRef.current = port;
    setIsSalvageYardConnected(true);
    // startHeartbeat();

    function normalizeUrl(url?: string | null): string | null {
      if (!url || typeof url !== "string") return null;
      try {
        const u = new URL(url);
        const pathname = u.pathname.replace(/\/+$/, "").toLowerCase();
        return `${u.origin}${pathname}`;
      } catch {
        return url.trim().toLowerCase().replace(/\/+$/, "");
      }
    }
    function parseOriginAndPath(
      url?: string | null
    ): { origin: string; path: string } | null {
      if (!url) return null;
      try {
        const u = new URL(url);
        const path = u.pathname.replace(/\/+$/, "").toLowerCase();
        return { origin: u.origin.toLowerCase(), path };
      } catch {
        // Fallback: try to split roughly
        try {
          const lower = url.toLowerCase();
          const match = lower.match(/^(https?:\/\/[^/]+)(\/.*)?$/);
          if (match) {
            const origin = match[1];
            const path = (match[2] || "").replace(/\/+$/, "");
            return { origin, path };
          }
        } catch {}
        return null;
      }
    }
    function urlsMatch(
      expectedUrl?: string | null,
      providedUrl?: string | null
    ): boolean {
      const exp = parseOriginAndPath(expectedUrl);
      const prv = parseOriginAndPath(providedUrl);
      if (!exp || !prv) return true; // if we cannot parse confidently, don't block
      if (exp.origin !== prv.origin) return false;
      if (exp.path === prv.path) return true;
      // Accept provided paths that are deeper under the expected profile path,
      // ensuring a segment boundary (e.g., '/in/username' vs '/in/username/recent-activity/all')
      if (prv.path.startsWith(exp.path + "/")) return true;
      return false;
    }

    port.onMessage.addListener((message: any) => {
      console.info("[SalvageYard] recieved extension message", message);
      if (message.action === "SUBMIT_BATCH") {
        const providedProfileUrl =
          message?.data?.meta?.providedMeta?.profileUrl ||
          message?.meta?.providedMeta?.profileUrl ||
          message?.data?.meta?.profileUrl ||
          message?.meta?.profileUrl ||
          null;
        const expected = normalizeUrl(expectedProfileUrlRef.current);
        const provided = normalizeUrl(providedProfileUrl);

        if (expected && provided && !urlsMatch(expected, provided)) {
          const posts = Array.isArray(message?.data?.posts)
            ? message.data.posts
            : [];
          console.warn(
            "[SalvageYard] Skipping batch due to profile URL mismatch",
            { expected, provided, count: posts.length }
          );
          setSkippedPosts((prev) => [
            ...prev,
            ...posts.map((p: any) => ({
              url: p?.url,
              text: p?.text,
              reason:
                "Profile URL mismatch between active profile and batch metadata",
            })),
          ]);
          // Do not submit this batch
          return;
        }
        submitBatch(message.data.posts, reportUUID, profileUUID);
      }
      if (message.action === "SCRAPE_COMPLETE") {
        // Mark complete only when there are no pending batches still saving
        if (pendingSavesRef.current <= 0) {
          setStoringPosts(false);
          setSuccess(true);
        }
        port.disconnect();
        portRef.current = null;
        setIsSalvageYardConnected(false);
      }
      if (message.action === "ERROR") {
        const error = message?.error
          ? String(message.error)
          : "Unknown extension error";
        console.error("[SalvageYard] onMessage ERROR", error);
        setError(error);
        port.disconnect();
        portRef.current = null;
        setIsSalvageYardConnected(false);
      }
    });
    console.info("[SalvageYard] port listener attached");

    port.onDisconnect.addListener(() => {
      if (chromeApi?.runtime?.lastError?.message) {
        const msg = `Port disconnected: ${chromeApi.runtime.lastError.message}`;
        console.error("[SalvageYard]", msg);
        setError(msg);
      } else {
        const msg = "Port disconnected unexpectedly.";
        console.error("[SalvageYard]", msg);
        setError(msg);
      }
      port.disconnect();
      portRef.current = null;
      setIsSalvageYardConnected(false);
    });
    console.info("[SalvageYard] port disconnect listener attached");

    port.postMessage({
      type: "INIT",
      payload: {
        reportId: reportUUID,
        profileId: profileUUID,
        content: profileContent,
        blueprint,
      },
    });
    console.info("[SalvageYard] port message sent");

    return null;
  };

  const submitBatch = async (
    batch: CreatePostApi[],
    reportUUID: string,
    profileUUID: string
  ) => {
    const MAX_POST_TEXT_LENGTH = 2000;
    const BATCH_SIZE = 50;
    const MAX_RETRIES = 5;
    const POST_URL_PARAMS_BLACKLIST = [
      "eav",
      "m_entstream_source",
      "_ft_",
      "__tn__",
      "paipv",
      "refid",
    ];

    const createBatches = (items: any[], batchSize: number) => {
      const batches = [];
      for (let i = 0; i < items.length; i += batchSize) {
        batches.push(items.slice(i, i + batchSize));
      }
      return batches;
    };

    const savePostBatch = async (payload: CreatePostApi[]) => {
      const response = await requestApi.post(
        `${API_URL}/manual_scrape/items`,
        payload
      );
      return response.data;
    };

    const retry = async (fn: Function, maxRetries: number) => {
      for (let i = 0; i < maxRetries; i++) {
        try {
          return await fn();
        } catch (error) {
          console.error(error);
          if (i < maxRetries - 1) {
            await new Promise((res) => setTimeout(res, i * 1000));
          }
        }
      }
      throw new Error("Max retries reached");
    };

    const isPayloadTooLarge = (error: any) => {
      const status = error?.response?.status;
      const message = String(error?.message || "");
      const dataMsg = String(error?.response?.data || "");
      if (status === 413) return true;
      if (/request[_\s-]*too[_\s-]*large/i.test(message)) return true;
      if (/http content length exceeded/i.test(message)) return true;
      if (/payload.+too.+large/i.test(message)) return true;
      if (/content.+too.+large/i.test(message)) return true;
      if (/entity.+too.+large/i.test(message)) return true;
      if (/request[_\s-]*too[_\s-]*large/i.test(dataMsg)) return true;
      if (/http content length exceeded/i.test(dataMsg)) return true;
      if (/payload.+too.+large/i.test(dataMsg)) return true;
      if (/content.+too.+large/i.test(dataMsg)) return true;
      if (/entity.+too.+large/i.test(dataMsg)) return true;
      return false;
    };

    // Preflight size check against API Gateway 10MB limit (with margin)
    // Conservative safety limit below API Gateway 10MB to account for headers/overhead
    const MAX_SAFE_REQUEST_BYTES = 9_000_000; // ~9MB
    const getPayloadBytes = (payload: unknown): number => {
      try {
        const json = JSON.stringify(payload);
        if (typeof TextEncoder !== "undefined") {
          return new TextEncoder().encode(json).length;
        }
        return json.length;
      } catch {
        return Number.MAX_SAFE_INTEGER;
      }
    };

    const postWithAdaptiveSplit = async (
      payload: CreatePostApi[]
    ): Promise<any[]> => {
      // Preflight: split if uncompressed payload likely exceeds safe threshold
      const approxBytes = getPayloadBytes(payload);
      if (payload.length > 1 && approxBytes > MAX_SAFE_REQUEST_BYTES) {
        console.warn("[SalvageYard] Pre-splitting batch due to size check", {
          originalSize: payload.length,
          approxBytes,
        });
        const mid = Math.floor(payload.length / 2);
        const left = payload.slice(0, mid);
        const right = payload.slice(mid);
        const [savedLeft, savedRight] = await Promise.all([
          postWithAdaptiveSplit(left),
          postWithAdaptiveSplit(right),
        ]);
        return [...savedLeft, ...savedRight];
      }
      try {
        const saved = await retry(() => savePostBatch(payload), MAX_RETRIES);
        return saved;
      } catch (err) {
        if (isPayloadTooLarge(err) && payload.length > 1) {
          console.warn("[SalvageYard] Payload too large, splitting batch", {
            originalSize: payload.length,
          });
          const mid = Math.floor(payload.length / 2);
          const left = payload.slice(0, mid);
          const right = payload.slice(mid);
          const [savedLeft, savedRight] = await Promise.all([
            postWithAdaptiveSplit(left),
            postWithAdaptiveSplit(right),
          ]);
          return [...savedLeft, ...savedRight];
        }
        throw err;
      }
    };

    const sanitizeUrl = (url: string) => {
      const params =
        url.split("?").length > 1 ? url.split("?")[1].split("&").sort() : [];
      const sanitizedParams = params
        .filter(
          (param) => !POST_URL_PARAMS_BLACKLIST.includes(param.split("=")[0])
        )
        .join("&");
      return `${url.split("?")[0]}?${sanitizedParams}`;
    };

    const batches = createBatches(
      batch.filter((p) => p.url.trim().length > 0),
      BATCH_SIZE
    ).map((batch) =>
      batch.map((payload) => ({
        ...payload,
        url: sanitizeUrl(payload.url),
        media: payload.media
          .map((m: MediaRequest) => {
            if (!m.external_url) return null;
            // if (!m.internal_url) return null;
            return m;
          })
          .filter((p: MediaRequest) => p),
        text: payload.text.substring(0, MAX_POST_TEXT_LENGTH),
        report_uuid: reportUUID,
        profile_uuid: profileUUID,
      }))
    );

    const savedPosts: any[][] = [];
    const saveAdaptively = async (batchPayload: CreatePostApi[]) => {
      const savedBatch = await postWithAdaptiveSplit(batchPayload);
      savedPosts.push(savedBatch);
    };

    try {
      setStoringPosts(true);
      pendingSavesRef.current += batches.length;
      await Promise.all(
        batches.map(async (b) => {
          await saveAdaptively(b);
          pendingSavesRef.current -= 1;
        })
      );
      const flattenedPosts = savedPosts.flat();
      setStoredPosts((prevPosts) => [...prevPosts, ...flattenedPosts]);
      if (pendingSavesRef.current <= 0) setStoringPosts(false);
      if (pendingSavesRef.current <= 0 && portRef.current == null)
        setSuccess(true);
    } catch (error) {
      console.error("Could not save batch", error);
      setSuccess(false);
      setStoringPosts(false);
      pendingSavesRef.current = 0;
    }
  };

  return {
    salvageYardScrape,
    isSalvageYardConnected,
    storingPosts,
    success,
    error,
    storedPosts,
    skippedPosts,
  };
}
